import React from 'react';
import { Link } from 'react-router-dom';
import { Package, Truck, Bike, RotateCcw, ArrowRight, Check, MapPin, Building2, Shield, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';

const steps = [
  {
    number: '01',
    icon: Package,
    title: 'Choose Your Kit',
    description: 'Select a curated gear bundle (helmet, jacket, gloves) or individual items tailored to your ride.',
    details: [
      'Professional Grade ECE-Certified Helmets',
      'All-Weather CE-Armored Jackets',
      'Premium Riding Gloves',
      'Curated from top brands like Fulmer, LS2, or equivalent'
    ]
  },
  {
    number: '02',
    icon: Truck,
    title: 'Direct-to-Hotel & Airbnb Delivery',
    description: 'We ship your gear directly to your hotel, Airbnb, or any US address. Your kit arrives 1–2 days before your rental begins — ready when you are.',
    details: [
      'Ship to any hotel concierge',
      'Airbnb & vacation rental friendly',
      'Free standard shipping (7+ days advance)',
      'Expedited delivery available (+$50)'
    ],
    highlight: true
  },
  {
    number: '03',
    icon: Bike,
    title: 'Gear Up & Ride',
    description: 'Enjoy your journey with premium, sanitized protection. Every piece is professionally cleaned and inspected before delivery.',
    details: [
      'CE Level 2 certified protection',
      'Professionally cleaned & inspected',
      'Fitted for comfort and safety',
      '24/7 support during your rental'
    ]
  },
  {
    number: '04',
    icon: RotateCcw,
    title: 'Easy Return',
    description: 'Use the included prepaid label to ship the gear back from any UPS or FedEx location. No cleaning required — we handle that.',
    details: [
      'Prepaid shipping label included',
      'Drop off at any UPS/FedEx',
      '2-day buffer for returns',
      'No cleaning required'
    ]
  }
];

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-white pt-24" data-testid="how-it-works-page">
      {/* Hero Section */}
      <section className="px-6 md:px-12 py-16 border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-4">The Process</p>
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold uppercase tracking-tight mb-6 text-gray-900">
            How It <span className="text-[#C6A87C]">Works</span>
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Renting premium motorcycle gear has never been easier. Four simple steps to get you on the road with world-class protection.
          </p>
        </div>
      </section>

      {/* Direct Delivery Highlight Banner */}
      <section className="px-6 md:px-12 py-8 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-[#C6A87C]" />
            <div className="text-center md:text-left">
              <p className="font-['Barlow_Condensed'] font-bold uppercase tracking-tight text-gray-900">Hotel Delivery</p>
              <p className="text-xs text-gray-500">Ship to your hotel concierge</p>
            </div>
          </div>
          <div className="hidden md:block w-px h-12 bg-gray-300"></div>
          <div className="flex items-center gap-3">
            <MapPin className="w-8 h-8 text-[#C6A87C]" />
            <div className="text-center md:text-left">
              <p className="font-['Barlow_Condensed'] font-bold uppercase tracking-tight text-gray-900">Airbnb Friendly</p>
              <p className="text-xs text-gray-500">Vacation rentals welcome</p>
            </div>
          </div>
          <div className="hidden md:block w-px h-12 bg-gray-300"></div>
          <div className="flex items-center gap-3">
            <Truck className="w-8 h-8 text-[#C6A87C]" />
            <div className="text-center md:text-left">
              <p className="font-['Barlow_Condensed'] font-bold uppercase tracking-tight text-gray-900">Free Shipping</p>
              <p className="text-xs text-gray-500">Book 7+ days in advance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="px-6 md:px-12 py-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="space-y-16">
            {steps.map((step, index) => (
              <div 
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`step-${index + 1}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-6xl font-['Barlow_Condensed'] font-bold text-gray-200">
                      {step.number}
                    </span>
                    <div className={`w-16 h-16 flex items-center justify-center ${
                      step.highlight ? 'bg-[#1a1a1a]' : 'bg-gray-100'
                    }`}>
                      <step.icon className={`w-8 h-8 ${step.highlight ? 'text-white' : 'text-gray-700'}`} strokeWidth={1.5} />
                    </div>
                  </div>
                  <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4 text-gray-900">
                    {step.title}
                  </h2>
                  <p className="text-lg text-gray-500 mb-6">
                    {step.description}
                  </p>
                  <ul className="space-y-3">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-gray-700">
                        <Check className="w-5 h-5 text-[#C6A87C] flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Visual */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="relative">
                    <div className={`aspect-[4/3] border flex items-center justify-center ${
                      step.highlight 
                        ? 'bg-gray-50 border-[#C6A87C]/30' 
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <step.icon className={`w-32 h-32 ${step.highlight ? 'text-gray-300' : 'text-gray-200'}`} strokeWidth={1} />
                    </div>
                    {/* Decorative corner */}
                    <div className={`absolute -bottom-4 -right-4 w-24 h-24 border-2 ${
                      step.highlight ? 'border-[#C6A87C]/50' : 'border-gray-200'
                    }`}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Security Section */}
      <section className="px-6 md:px-12 py-16 bg-gray-50 border-t border-gray-200" data-testid="trust-security-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-[#C6A87C]" />
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-gray-900">
                Trust & Security
              </h2>
            </div>
            <p className="text-gray-500 max-w-2xl mx-auto">
              We prioritize high-quality gear and your safety. Here&apos;s how we protect both you and our equipment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* ID Verification */}
            <div className="bg-white border border-gray-200 p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gray-100 flex items-center justify-center">
                  <Shield className="w-7 h-7 text-gray-700" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-gray-900">
                  ID Verification
                </h3>
              </div>
              <p className="text-gray-500 mb-4">
                All renters must complete a quick ID verification before their first rental. This ensures the safety of our community and the quality of our gear.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-[#C6A87C]" />
                  <span>Valid government-issued photo ID</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-[#C6A87C]" />
                  <span>Quick 2-minute verification process</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-[#C6A87C]" />
                  <span>Data encrypted and never shared</span>
                </li>
              </ul>
            </div>

            {/* Security Deposit */}
            <div className="bg-white border border-gray-200 p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gray-100 flex items-center justify-center">
                  <CreditCard className="w-7 h-7 text-gray-700" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-gray-900">
                  Security Deposit
                </h3>
              </div>
              <p className="text-gray-500 mb-4">
                A refundable security hold is placed on your card at checkout. This protects against damage and late returns, and is fully released upon successful return.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-[#C6A87C]" />
                  <span>$150 refundable hold, returned after gear inspection</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-[#C6A87C]" />
                  <span>Released within 3-5 business days of return</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-[#C6A87C]" />
                  <span>Optional Damage Protection ($25/day) reduces liability</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 py-20 border-t border-gray-200">
        <div className="max-w-[1400px] mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight mb-6 text-gray-900">
            Ready to <span className="text-[#C6A87C]">Ride?</span>
          </h2>
          <p className="text-gray-500 mb-8 max-w-xl mx-auto">
            Browse our collection of premium gear and hit the road with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button 
                className="bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider px-8 py-6 text-base"
                data-testid="browse-gear-btn"
              >
                Browse Gear
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button 
                variant="outline"
                className="border-gray-300 text-gray-700 hover:border-[#1a1a1a] hover:bg-gray-50 font-bold uppercase tracking-wider px-8 py-6 text-base"
                data-testid="view-faq-btn"
              >
                View FAQ
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
